// One spelling per region. The UCO app's zone names are typed by hand, so the same governorate
// shows up as "Beja" and "Béja", or "Medenine" and "edenine". The first segment of a zone path
// is matched to the region list below (accents, case, spaces and hyphens ignored, one typo or a
// missing first letter allowed); anything that does not match is left as it was.

/** How each region is written everywhere on the site. */
export const REGIONS = [
  'Tunis', 'Tunis Center', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan', 'Bizerte', 'Beja', 'Jendouba', 'Kef',
  'Siliana', 'Sousse', 'Monastir', 'Mahdia', 'Sfax', 'Kairouan', 'Kasserine', 'Sidi Bouzid', 'Gabes', 'Medenine', 'Tataouine',
  'Gafsa', 'Tozeur', 'Kebili',
];
/** Other names people use for a region. */
const ALIASES: Record<string, string> = { lekef: 'Kef', elkef: 'Kef', lamanouba: 'Manouba', tuniscentre: 'Tunis Center', tunisville: 'Tunis' };

const keyOf = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');
const BY_KEY = new Map(REGIONS.map((r) => [keyOf(r), r] as const));

/** Edit distance, stopping early once it is above 1. */
function withinOneEdit(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

const cache = new Map<string, string>();
/** "Béja" -> "Beja", "edenine" -> "Medenine", "Le Kef" -> "Kef"; unknown names unchanged. */
export function canonRegion(name: string): string {
  const hit = cache.get(name);
  if (hit !== undefined) return hit;
  const k = keyOf(name);
  let out = BY_KEY.get(k) || ALIASES[k];
  if (!out && k.length >= 5) {
    const near = REGIONS.filter((r) => withinOneEdit(k, keyOf(r)));
    if (near.length === 1) out = near[0];
  }
  const res = out || name.trim();
  cache.set(name, res);
  return res;
}

/** "Béja, Béja Nord" -> "Beja, Béja Nord": only the region (first segment) is rewritten. */
export function canonZonePath(path: string): string {
  const i = path.indexOf(',');
  const head = i < 0 ? path : path.slice(0, i);
  const fixed = canonRegion(head.trim());
  if (fixed === head.trim()) return path;
  return i < 0 ? fixed : fixed + path.slice(i);
}
